<template>
  <div class="cost-center-select w-100" :class="{ 'h-100': !isLISA3 }">
    <TreeSelect
      v-bind="$attrs"
      v-model="costCenterId"
      v-model:modal-visible="selectModalVisibility"
      :entity-name="t('components.shared.searchSelect.CostCenterSelect.title')"
      :multiple="selectionMode === 'multiple'"
      :elements="costCenters"
      :show-inline-create="showInlineCreate"
      :valid="valid"
      :loading="loading || ancestorContextLoading"
      :placeholder-options="placeholderOptions"
      :disabled="disabled"
      create-access-right="systemSettings.admin"
      :disabled-nodes="disabledCostCenters"
      :clearable="clearable"
      :auto-select-first="autoSelectFirst"
      :preferred-scroll-id="preferredScrollId"
      @show-create-modal="showCreateModal"
    >
      <template #rowControls="slotProps">
        <slot name="rowControls" v-bind="slotProps" />
      </template>
      <template v-if="$slots.append" #append>
        <slot name="append" />
      </template>
      <template v-if="$slots.append" #prepend>
        <slot name="prepend" />
      </template>
    </TreeSelect>
    <OGCostCenterCreate
      v-if="createModalVisible"
      :show-in-modal="true"
      :site-id="
        props.variables.siteIds && props.variables.siteIds.length === 1
          ? (props.variables.siteIds[0] ?? undefined)
          : undefined
      "
      @created="setCostCenter"
      @cancel="createModalVisible = false"
    />
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import { computed, ref, type PropType } from "vue";
import { useCostCentersForSelectQuery, type OGCostCenterCreateMutation } from "@/graphql";
import TreeSelect from "@/components/shared/searchSelect/treeSelect/TreeSelect.vue";
import OGCostCenterCreate from "../../views/OGCostCenterCreate.vue";
import { isLISA3 } from "@/composables/useLISA3";

const props = defineProps({
  variables: {
    type: Object as PropType<
      Partial<{
        ids: number[];
        siteIds: number[];
        hierarchyMinLevel: number;
        selfAndDescendantsOfTPId: number;
        selfAndDescendantsOfCCId: number;
        notSelfAndNotDescendantsOfCCId: number;
      }>
    >,
    required: false,
    default: () => ({}),
  },
  selectionMode: {
    type: String as PropType<"single" | "multiple">,
    required: false,
    default: "single",
  },
  autoSelectFirst: {
    type: Boolean,
    required: false,
    default: false,
  },
  preferredScrollId: {
    type: Number,
    required: false,
    default: undefined,
  },
  placeholderOptions: {
    type: Object as PropType<{ label: string; variant: string }>,
    required: false,
    default: null,
  },
  showInlineCreate: {
    type: Boolean,
    required: false,
    default: true,
  },
  valid: {
    type: Boolean,
    required: false,
    default: undefined,
  },
  clearable: {
    type: Boolean,
    required: false,
    default: true,
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  disabledNodes: {
    type: Array as PropType<
      {
        key: string | number;
        includeChildren: boolean;
      }[]
    >,
    required: false,
    default: undefined,
  },
});

const { result, loading } = useCostCentersForSelectQuery(computed(() => props.variables));

const hasDescendantRestriction = computed(
  () =>
    props.variables.selfAndDescendantsOfCCId != null ||
    props.variables.selfAndDescendantsOfTPId != null,
);

const { result: ancestorContextResult, loading: ancestorContextLoading } =
  useCostCentersForSelectQuery(
    computed(() => ({ siteIds: props.variables.siteIds })),
    computed(() => ({ enabled: hasDescendantRestriction.value })),
  );

const ancestorCostCenters = computed(() => {
  if (!hasDescendantRestriction.value) {
    return [];
  }

  const selectableCostCenters = result.value?.OGCostCenters ?? [];
  const selectablePaths = new Set(selectableCostCenters.map(({ path }) => path));
  const ancestorPaths = new Set(
    selectableCostCenters.flatMap(({ path }) => {
      const pathParts = path.split(".");

      return pathParts.slice(0, -1).map((_, index) => pathParts.slice(0, index + 1).join("."));
    }),
  );

  return (ancestorContextResult.value?.OGCostCenters ?? []).filter(
    ({ path }) => ancestorPaths.has(path) && !selectablePaths.has(path),
  );
});

const costCenters = computed(() => [
  ...ancestorCostCenters.value,
  ...(result.value?.OGCostCenters ?? []),
]);

const disabledCostCenters = computed(() => [
  ...(props.disabledNodes ?? []),
  ...ancestorCostCenters.value.map(({ id }) => ({ key: id, includeChildren: false })),
]);

const costCenterId = defineModel("modelValue", {
  type: [Array, Number] as PropType<number | number[] | undefined>,
  default: undefined,
});

const createModalVisible = ref(false);

function showCreateModal() {
  createModalVisible.value = true;
}

const selectModalVisibility = ref(false);

function setCostCenter(cc: OGCostCenterCreateMutation["OGCostCenterCreate"]) {
  selectModalVisibility.value = false;
  createModalVisible.value = false;
  costCenterId.value = cc.id;
}

const { t } = useTranslation();
</script>

<style lang="scss">
.p-inputgroup > .cost-center-select {
  flex: 1 1 0;
  width: 0 !important;
  min-width: 0;
  overflow: hidden;
}

.cost-center-select > div,
.cost-center-select .input-field {
  width: 100%;
  min-width: 0;
}
</style>
